import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Phone, Mail, MessageSquare, CheckCircle, TrendingUp, FileText, Upload, Briefcase, User, DollarSign, FolderOpen } from 'lucide-react';

const ActivityDashboardWidget = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityData, setEntityData] = useState({});

  useEffect(() => {
    fetchRecentActivities();
  }, []);

  const fetchRecentActivities = async () => {
    try {
      const response = await api.get('/activities', { params: { limit: 20 } });
      const activitiesData = response.data;
      setActivities(activitiesData);
      
      // Fetch entity names for each activity
      await fetchEntityNames(activitiesData);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntityNames = async (activitiesData) => {
    const entityMap = {};
    const fetchPromises = [];

    // Group activities by entity type
    const entitiesByType = activitiesData.reduce((acc, activity) => {
      const type = activity.entity_type;
      if (!acc[type]) acc[type] = new Set();
      acc[type].add(activity.entity_id);
      return acc;
    }, {});

    // Fetch entity names
    for (const [entityType, entityIds] of Object.entries(entitiesByType)) {
      const endpoint = getEntityEndpoint(entityType);
      if (endpoint) {
        fetchPromises.push(
          api.get(endpoint).then(response => {
            response.data.forEach(entity => {
              const key = `${entityType}_${entity.id}`;
              entityMap[key] = getEntityName(entity, entityType);
            });
          }).catch(() => {}) // Ignore errors for individual entity types
        );
      }
    }

    await Promise.all(fetchPromises);
    setEntityData(entityMap);
  };

  const getEntityEndpoint = (entityType) => {
    const endpoints = {
      Lead: '/leads',
      Proposal: '/proposals',
      Invoice: '/invoices',
      Project: '/projects',
      Company: '/companies',
      Contact: '/contacts',
      Task: '/tasks'
    };
    return endpoints[entityType];
  };

  const getEntityName = (entity, entityType) => {
    const nameFields = {
      Lead: entity.potential_project_name,
      Proposal: `${entity.prepared_for || ''} - ${entity.project_property || ''}`.trim(),
      Invoice: `${entity.client_name || ''} - ${entity.invoice_number || ''}`.trim(),
      Project: entity.project_name,
      Company: entity.company_name,
      Contact: `${entity.first_name || ''} ${entity.last_name || ''}`.trim(),
      Task: entity.title
    };
    return nameFields[entityType] || 'Unknown';
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      Lead: '🧩',
      Proposal: '📄',
      Invoice: '🧾',
      Project: '📁',
      Company: '🏢',
      Contact: '👤',
      Task: '✅'
    };
    return icons[entityType] || '📌';
  };

  const getActivityIcon = (type) => {
    const icons = {
      call: <Phone className="h-4 w-4" />,
      meeting: <MessageSquare className="h-4 w-4" />,
      email: <Mail className="h-4 w-4" />,
      note: <FileText className="h-4 w-4" />,
      status_change: <TrendingUp className="h-4 w-4" />,
      task_completed: <CheckCircle className="h-4 w-4" />,
      proposal_sent: <FileText className="h-4 w-4" />,
      proposal_accepted: <CheckCircle className="h-4 w-4" />,
      file_uploaded: <Upload className="h-4 w-4" />,
    };
    return icons[type] || <Clock className="h-4 w-4" />;
  };

  const getActivityColor = (type) => {
    const colors = {
      call: 'bg-blue-100 text-blue-800',
      meeting: 'bg-purple-100 text-purple-800',
      email: 'bg-green-100 text-green-800',
      note: 'bg-gray-100 text-gray-800',
      status_change: 'bg-orange-100 text-orange-800',
      task_completed: 'bg-green-100 text-green-800',
      proposal_sent: 'bg-yellow-100 text-yellow-800',
      proposal_accepted: 'bg-green-100 text-green-800',
      file_uploaded: 'bg-blue-100 text-blue-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatFieldChange = (activity) => {
    const metadata = activity.metadata || {};
    
    // Helper to convert legacy UUID status values to readable labels
    const convertStatusValue = (value) => {
      // If it looks like a UUID (contains dashes and is long), return 'Unknown'
      if (typeof value === 'string' && value.length > 20 && value.includes('-')) {
        return 'Unknown';
      }
      return value;
    };
    
    // Handle status changes
    if (activity.activity_type === 'status_change' && metadata.old_status && metadata.new_status) {
      const oldStatus = convertStatusValue(metadata.old_status);
      const newStatus = convertStatusValue(metadata.new_status);
      return `Status changed: ${oldStatus} → ${newStatus}`;
    }
    
    // Handle other field changes
    if (metadata.field_changed && metadata.old_value !== undefined && metadata.new_value !== undefined) {
      const oldValue = convertStatusValue(metadata.old_value);
      const newValue = convertStatusValue(metadata.new_value);
      return `${metadata.field_changed} changed: ${oldValue} → ${newValue}`;
    }
    
    // Fallback to title/description
    return activity.title || activity.description;
  };

  const getUserName = (activity) => {
    // TODO: Fetch user name from user_id if available
    // For now, return generic text or extract from metadata
    if (activity.metadata && activity.metadata.user_name) {
      return activity.metadata.user_name;
    }
    return activity.user_id || 'System';
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Recent Activity</CardTitle>
          <p className="text-sm text-slate-500 mt-1">Latest updates across all entities</p>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-500">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">Recent Activity</CardTitle>
        <p className="text-sm text-slate-500 mt-1">Latest updates across all entities</p>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Clock className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <p>No recent activity</p>
          </div>
        ) : (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {activities.map((activity) => {
              const entityKey = `${activity.entity_type}_${activity.entity_id}`;
              const entityName = entityData[entityKey] || 'Unknown';
              const entityIcon = getEntityIcon(activity.entity_type);
              const fieldChange = formatFieldChange(activity);
              const userName = getUserName(activity);
              const timestamp = formatDate(activity.created_at);

              return (
                <div 
                  key={activity.id} 
                  className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-2 px-2 py-2 rounded transition-colors cursor-pointer"
                >
                  {/* Entity Icon */}
                  <div className="flex-shrink-0 text-2xl mt-0.5">
                    {entityIcon}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Entity Type and Name */}
                    <div className="font-medium text-sm text-slate-900 mb-1">
                      {activity.entity_type} — {entityName}
                    </div>
                    
                    {/* Field Change */}
                    <div className="text-sm text-slate-600 mb-1">
                      {fieldChange}
                    </div>
                    
                    {/* User and Timestamp */}
                    <div className="text-xs text-slate-500">
                      Updated by {userName} · {timestamp}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityDashboardWidget;
